import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useLang } from '../contexts/LanguageContext';
import { NotificationBell } from './NotificationBell';
import { ShoppingBag, User, Menu, X, Heart } from 'lucide-react';

const LOGO_URL = '/biolosophy-logo.png';

export default function Header() {
  const { isAuthenticated } = useAuth();
  const { itemCount } = useCart();
  const { lang, setLang, t } = useLang();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/filosofia', label: { it: 'La Nostra Filosofia', en: 'Our Philosophy' } },
    { path: '/shop?category=donna', label: { it: 'Donna', en: 'Women' } },
    { path: '/shop?category=uomo', label: { it: 'Uomo', en: 'Men' } },
    { path: '/shop?category=bambino', label: { it: 'Bambino', en: 'Kids' } },
    { path: '/shop', label: { it: 'Shop', en: 'Shop' } },
    { path: '/club', label: { it: 'Club Biolosophy', en: 'Club Biolosophy' } },
    { path: '/pre-loved', label: { it: 'Pre-Loved', en: 'Pre-Loved' } },
  ];

  const LangSwitcher = () => (
    <div className="flex items-center gap-1 text-xs font-medium" data-testid="lang-switcher">
      <button
        onClick={() => setLang('it')}
        data-testid="lang-it-btn"
        className={`px-1.5 py-0.5 transition-colors ${lang === 'it' ? 'text-[#1C1917] underline underline-offset-4' : 'text-[#A8A29E] hover:text-[#1C1917]'}`}
      >
        IT
      </button>
      <span className="text-[#E5E0D8]">|</span>
      <button
        onClick={() => setLang('en')}
        data-testid="lang-en-btn"
        className={`px-1.5 py-0.5 transition-colors ${lang === 'en' ? 'text-[#1C1917] underline underline-offset-4' : 'text-[#A8A29E] hover:text-[#1C1917]'}`}
      >
        EN
      </button>
    </div>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#1C1917]/10">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" data-testid="header-logo" className="flex items-center shrink-0">
            <img
              src={LOGO_URL}
              alt="biolosophy"
              className="h-10 md:h-14"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.it.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-sm font-medium text-[#1C1917] transition-colors hover:text-[#4A5D23] whitespace-nowrap"
              >
                {t(link.label)}
              </Link>
            ))}
          </nav>

          {/* Right Icons */}
          <div className="flex items-center gap-4">
            <div className="hidden md:block">
              <LangSwitcher />
            </div>
            {isAuthenticated && <NotificationBell />}
            {isAuthenticated && (
              <Link
                to="/account/wishlist"
                data-testid="header-wishlist"
                className="hidden md:block text-[#1C1917] hover:text-[#4A5D23] transition-colors"
              >
                <Heart className="w-5 h-5" />
              </Link>
            )}

            <Link
              to={isAuthenticated ? '/account' : '/login'}
              data-testid="header-account"
              className="text-[#1C1917] hover:text-[#4A5D23] transition-colors"
            >
              <User className="w-5 h-5" />
            </Link>

            <Link
              to="/cart"
              data-testid="header-cart"
              className="relative text-[#1C1917] hover:text-[#4A5D23] transition-colors"
            >
              <ShoppingBag className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-[#C05746] text-white text-xs rounded-full">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-[#1C1917] transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#FAF9F6] border-t border-[#E5E0D8] animate-slide-up">
          <nav className="px-6 py-4 space-y-4">
            <LangSwitcher />
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 text-[#1C1917] font-medium hover:text-[#4A5D23] transition-colors"
              >
                {t(link.label)}
              </Link>
            ))}
            <div className="pt-4 border-t border-[#E5E0D8]">
              <Link
                to={isAuthenticated ? '/account' : '/login'}
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 text-[#1C1917] font-medium hover:text-[#4A5D23] transition-colors"
              >
                {isAuthenticated ? t({ it: 'Il Mio Account', en: 'My Account' }) : t({ it: 'Accedi', en: 'Sign In' })}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
